import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../../../components/forms/Select/Select";
import AsyncSearchSelect from "../../../../components/forms/Select/AsyncSearchSelect";
import { getTopics } from "../../../../services/Core/TopicService";
import { getSubtopics } from "../../../../services/Core/SubtopicServices";
import KaTeXComponent from "../../../../components/KaTeXComponent/KaTeXComponent";
import { ExclamationCircleIcon, ExclamationTriangleIcon, PlusIcon } from "@heroicons/react/24/outline";
import Option from "./Option";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ALP, errorFields, fieldList, invalidSelectStyles } from "./QuestionUtils";
import ShortEditableContent from "../../../../components/KaTeXComponent/ShortEditableContent";
import { DifficultyPicker } from "../../../../components/Difficulty/Difficulty";
import { QuestionStatusBadge } from "../../../../components/QuestionList/questionStatus";

const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
const errorText = "mt-1 block text-xs font-medium text-red-600 empty:hidden";

/**
 * One question's form. `errors`: translation keys by field (`answer_<uuid>` for an option; `topic` only
 * marks its picker), from the page's validation or a 400 incomplete. `required`: the question is in an
 * exam's subject, where the subtopic and difficulty are required too. `reviewNeeds`: required fields an
 * edited question still lacks (not refused on save: the question already lacked them).
 * `subject` (and `grade`) narrow the topics offered.
 */
export default function QuestionCard({
  isEdit,
  question,
  loading,
  purposes,
  grade,
  subject,
  errors,
  required = false,
  reviewNeeds = [],
  objectives,
  handleQuestionUpdate,
  handleOptionUpdate,
  addOption,
  removeOption,
}) {
  const [t] = useTranslation();
  // Prefill the pickers when editing (EditQuestion / EditDraftQuestion pass the names along).
  const [topicValue, setTopicValue] = useState(() =>
    question.topic && question.topic_name ? { value: question.topic, label: question.topic_name } : null
  );
  const [subtopicValue, setSubtopicValue] = useState(() =>
    question.subtopic && question.subtopic_name ? { value: question.subtopic, label: question.subtopic_name } : null
  );
  const [text, setText] = useState(question.body ?? "");
  const refTimer = useRef();
  const refPending = useRef(null);
  const [fastItems, setFastItems] = useState(question?.answer_keys);

  useEffect(() => {
    setFastItems(question?.answer_keys);
  }, [question?.answer_keys]);

  const fetchTopicOptions = useCallback(
    (search) => {
      return getTopics({ params: { grade, subject, search } }).then((res) => {
        const arr = Array.isArray(res) ? res : res.results || [];
        return arr;
      });
    },
    [grade, subject],
  );

  const fetchSubtopicOptions = useCallback(
    (search) => {
      if (!question.topic) return Promise.resolve([]);
      return getSubtopics({ params: { topic: question.topic, search } }).then(
        (res) => {
          const arr = Array.isArray(res) ? res : res.results || [];
          return arr;
        },
      );
    },
    [question.topic],
  );

  // Sends the debounced update now, once: on editor blur (Save/Next blur the editor before their click
  // runs, so they read the current body), after an image upload and on unmount.
  const flushSaveData = useCallback(() => {
    clearTimeout(refTimer.current);
    const pending = refPending.current;
    refPending.current = null;
    pending?.();
  }, []);

  useEffect(() => flushSaveData, [flushSaveData]);

  const debounceSaveData = useCallback(
    (field, value) => {
      if (refTimer.current) {
        clearTimeout(refTimer.current);
      }

      refPending.current = () => handleQuestionUpdate(field, value);
      refTimer.current = setTimeout(flushSaveData, 1000);
    },
    [handleQuestionUpdate, flushSaveData],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setFastItems((prevItems) => {
        const oldIndex = prevItems.findIndex((item) => item.uuid === active.id);
        const newIndex = prevItems.findIndex((item) => item.uuid === over.id);
        const newArr = arrayMove(prevItems, oldIndex, newIndex).map(
          (opt, i) => ({ ...opt, option: ALP[i % ALP.length] }),
        );
        handleQuestionUpdate("answer_keys", newArr);
        return newArr;
      });
    }
  };

  // What the save found missing, listed at the top of the card (a long card hides some of the fields).
  const missing = errorFields(errors);
  const hints = reviewNeeds.filter((field) => !errors?.[field]);
  const optionsInvalid =
    !!errors?.answer_keys || !!errors?.correct_answer || fastItems.some((answer) => errors?.[`answer_${answer.uuid}`]);
  const requiredMark = required ? " *" : "";

  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
      <div className="flex flex-col">
        <span className="flex flex-row flex-wrap items-center gap-2 pr-10 text-lg font-semibold tracking-tight text-slate-900">
          {t("my_questions.editor.question_number", { number: question.code ?? question.id + 1 })}{" "}
          {isEdit && <QuestionStatusBadge status={question?.status} className="tracking-normal" />}
        </span>
        <span className="mt-1 text-sm text-slate-500">
          {t("my_questions.editor.select_sections")}
        </span>
        {required && (
          <span className="mt-0.5 text-sm text-slate-500">{t("my_questions.editor.required_in_subject")}</span>
        )}
      </div>
      {missing.length > 0 && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t("my_questions.validation.missing_fields", { fields: fieldList(t, missing) })}</span>
        </div>
      )}
      {hints.length > 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
          <span>{t("my_questions.validation.review_needs", { fields: fieldList(t, hints) })}</span>
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="min-w-0">
          <Select
            className="min-w-0"
            placeholder={t("my_questions.editor.select_term")}
            ariaLabel={t("my_questions.editor.select_term")}
            loading={loading}
            isSearchable={true}
            value={question.purpose}
            options={purposes.map((p) => ({ value: p.uuid, label: p.name }))}
            onChange={(v) => handleQuestionUpdate("purpose", v.value)}
          />
        </div>
        <div className="min-w-0" data-invalid={errors?.topic ? "true" : undefined}>
          <AsyncSearchSelect
            className="min-w-0"
            placeholder={t("my_questions.editor.select_topic")}
            ariaLabel={t("my_questions.editor.select_topic")}
            customStyles={errors?.topic ? invalidSelectStyles : undefined}
            value={topicValue}
            fetchOptions={fetchTopicOptions}
            onChange={(v) => {
              setTopicValue(v);
              handleQuestionUpdate("topic", v ? v.value : "");
              // Kept with the question: the questions step is filled again from it after the final view.
              handleQuestionUpdate("topic_name", v ? v.label : "");
              setSubtopicValue(null);
              handleQuestionUpdate("subtopic", "");
              handleQuestionUpdate("subtopic_name", "");
            }}
          />
        </div>
        <div className="min-w-0" data-invalid={errors?.subtopic ? "true" : undefined}>
          <AsyncSearchSelect
            key={question.topic || "no-topic"}
            className="min-w-0"
            placeholder={t("my_questions.editor.select_subtopic")}
            ariaLabel={t("my_questions.editor.select_subtopic")}
            customStyles={errors?.subtopic ? invalidSelectStyles : undefined}
            isDisabled={!question.topic}
            value={subtopicValue}
            fetchOptions={fetchSubtopicOptions}
            onChange={(v) => {
              setSubtopicValue(v);
              handleQuestionUpdate("subtopic", v ? v.value : "");
              handleQuestionUpdate("subtopic_name", v ? v.label : "");
            }}
          />
          <span className={errorText}>{errors?.subtopic && t(errors.subtopic)}</span>
        </div>
        <div className="min-w-0">
          <Select
            className="min-w-0"
            placeholder={t("my_questions.editor.select_objective")}
            ariaLabel={t("my_questions.editor.select_objective")}
            loading={loading}
            isSearchable={true}
            value={question.objective}
            options={objectives.map((o) => ({ value: o.uuid, label: o.name }))}
            onChange={(v) => handleQuestionUpdate("objective", v.value)}
          />
        </div>
      </div>
      {/* The server derives the question's points from it (A 3, B 5, C 10); required in an exam's subject. */}
      <div className="mt-4" data-invalid={errors?.difficulty ? "true" : undefined}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm font-medium text-slate-700">
            {t("difficulty.label")}
            {requiredMark}
          </span>
          <DifficultyPicker
            value={question.difficulty ?? null}
            noneLabel={required ? undefined : t("difficulty.not_set")}
            onChange={(level) => handleQuestionUpdate("difficulty", level)}
            // Phones: 44px touch targets.
            className={`max-md:[&>button]:h-11 max-md:[&>button]:min-w-[2.75rem] ${errors?.difficulty ? "!ring-red-400" : ""}`}
          />
        </div>
        <span className={errorText}>{errors?.difficulty && t(errors.difficulty)}</span>
      </div>
      {/* The page's title says create or edit; this part holds the question and its answers. */}
      <span className="mb-4 mt-8 border-t border-slate-100 pt-6 text-base font-semibold text-slate-900">
        {t("my_questions.editor.content_heading")}
      </span>
      <span className="mb-1.5 text-sm font-medium text-slate-700">{t("my_questions.editor.name")}</span>
      <input
        type="text"
        aria-label={t("my_questions.editor.name")}
        className="mb-5 h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15"
        placeholder={t("my_questions.editor.name_placeholder")}
        value={question.name || ""}
        onChange={(e) => handleQuestionUpdate("name", e.target.value)}
      />
      <span className="mb-1.5 text-sm font-medium text-slate-700">{t("my_questions.editor.question_label")}</span>
      <div data-invalid={errors?.body ? "true" : undefined}>
        <ShortEditableContent
          ariaLabel={t("my_questions.upload.question_text")}
          sizeClassName={`min-h-[2.5rem]${errors?.body ? " !border-red-400" : ""}`}
          onChange={(e) => {
            setText(e);
            debounceSaveData("body", e);
            // A shown body error clears on the first keystroke: cleared by the flush on Save's mousedown
            // instead, it would vanish mid-click and move Save out from under the pointer.
            if (errors?.body) flushSaveData();
          }}
          onBlur={flushSaveData}
          // An inserted image lands while the editor is not focused, so no blur would send it.
          onUploadingChange={(uploading) => {
            if (!uploading) flushSaveData();
          }}
          value={text}
        />
      </div>
      <span className={errorText}>{errors?.body && t(errors.body)}</span>
      <div className="mt-3 flex min-h-[2.75rem] flex-col gap-0.5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
        <KaTeXComponent text={text} />
      </div>

      <div className="mt-8 flex flex-col" data-invalid={errors?.answer_keys || errors?.correct_answer ? "true" : undefined}>
        <span className={`mb-2 text-sm font-medium ${optionsInvalid ? "text-red-700" : "text-slate-700"}`}>
          {t("my_questions.editor.answer_options")}
        </span>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fastItems.map((i) => i.uuid)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-3">
              {fastItems.map((answer, i) => (
                <Option
                  key={answer.uuid}
                  answer={answer}
                  error={errors?.[`answer_${answer.uuid}`]}
                  invalidCorrect={!!errors?.correct_answer}
                  removeOption={() => removeOption(i)}
                  isDeletable={question.answer_keys.length > 2}
                  handleOptionUpdate={(field, value) =>
                    handleOptionUpdate(i, field, value)
                  }
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <span className={errorText}>{errors?.answer_keys && t(errors.answer_keys)}</span>
        <span className={errorText}>{errors?.correct_answer && t(errors.correct_answer)}</span>

        <button
          type="button"
          className={`-ml-2 mt-3 flex w-fit flex-row items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-600 transition duration-200 hover:bg-blue-50 hover:text-blue-700 max-md:min-h-[2.75rem] ${focusRing}`}
          onClick={() => addOption()}
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" /> {t("my_questions.editor.add_option")}
        </button>
      </div>
      <div className="mt-8 flex flex-col gap-2.5 border-t border-slate-100 pt-6">
        <span className="text-sm font-medium text-slate-500">{t("my_questions.editor.preview_options")}</span>
        {question.answer_keys.map((answer, i) => (
          <div key={i} className="flex flex-row items-center gap-3">
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                answer.is_correct
                  ? "bg-emerald-600 text-white shadow-sm shadow-emerald-600/25"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {answer.option}
            </div>
            <span className="min-w-0 text-sm text-slate-700">
              <KaTeXComponent text={answer.body} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
